/**
 * @file pricingCostingView.js
 * @version 1.6.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-19
 * @modified 2026-10-29
 * @description How the Pricing & Costing screen lays its rows out while
 *              someone works on them. Pure; client only (the server never lays
 *              rows out).
 *
 *   Holding a row in place (Cam, 19 Oct 2026 — "click into a field to type a
 *   price and it collapses the row and … scroll[s] away from that row in a
 *   microsecond or move[s] that row somewhere else"). The table is sorted and
 *   the pile tabs filtered by each row's pile, and the pile is worked out
 *   again on every choice and keystroke (utils/pricingCosting.pileOf): typing
 *   a price sends a Ready line to Check, so it jumped to the Check section —
 *   or out of the Ready tab altogether — with the input the person was typing
 *   in. So a row being edited is HELD: it keeps the pile it had when the edit
 *   began for placing (section, order, page, tab) until the person is done
 *   with it — clicks or tabs to something outside it, collapses it, moves on
 *   with J/K, confirms, or changes tab or filter. Then it is released and
 *   goes where it now belongs, highlighted, with a note saying where.
 *
 *   Grouping by customer › product (Cam, 19 Oct 2026 — "allow users to group
 *   order rows by customer, subgrouped by product … so user does not need to
 *   duplicate work"). A subgroup is exactly what a price request to a rep asks
 *   ONCE (utils/priceRequestGroups on the server: customer, item code else
 *   words, additive set, unit, pricing window) — each order line carries those
 *   keys as `group` — so the screen and the reps' emails agree. A subgroup
 *   shows its lines combined (one figure when they agree, else a range) and
 *   takes ONE sale price for every line that can take it; each line is then
 *   confirmed through the ordinary per-line confirm (same audit, guards, Undo).
 *
 *   1.2.0 (Cam, 19 Oct 2026 — "each product group shares a single row,
 *   expandable to allow cost price and sale price for whole group, or user can
 *   split out into separate cost and sale prices per order … Right click a cost
 *   or sale price for a product group … set cost for group / set sale for group
 *   / set both for group"): every rule here works on EITHER side — a group's
 *   cost as well as its sale is one value set on all its lines (each line's own
 *   choice, so a line edited on its own just makes the group "Differs"), with
 *   how many distinct figures there are, what to prefill a "Set … for group"
 *   with, and a line's side as it would read with a choice applied (to confirm
 *   straight after setting).
 *
 *   1.3.0 (Cam, 20 Oct 2026 — "show the delivery date of orders please in the
 *   item cell so instead of a range do comma separated"): deliveryDatesText —
 *   each date once, in order, DD/MM (DD/MM/YY when the years differ; one date
 *   alone in full).
 *
 *   1.4.0 (Cam, 20 Oct 2026 — "The expand and collapse all button should
 *   actually operate on the customer sections … collapsing a customer's
 *   section collapses the products within (expanding does not expand them
 *   though) … when one product/ product group is expanded all others are
 *   collapsed, (doesn't apply to customer sections)"): what is open, as one
 *   state — `open` (a line's options), `openGroups` (a product group's lines)
 *   and `collapsed` (customer sections folded to their heading) — and the
 *   moves on it: one product open at a time, customers collapsed / expanded
 *   one by one or all together; customerSummary for a heading that stands
 *   alone when its section is folded.
 *
 *   1.5.0 (Cam, 28 Oct 2026 — the redesign: D1 five stages, D2 the four-step
 *   strip, the month dropdown). lineSteps / groupSteps: Cost · Sale ·
 *   Confirmed · Invoiced, each done (green), with a rep (blue), needs a look
 *   (amber), not yet (grey) or not on this line — a group "2/3". A held row is
 *   placed by the STAGE it had when the edit began (holdRow is given the
 *   stage). rangeOptions / rangeOf: This month, the two before and the two
 *   after by name, Custom — the person's last choice remembered
 *   (useScreenPreference 'pricingCosting.range'); a named month stays that
 *   month, "This month" follows the calendar.
 *
 *   1.5.1 (2026-10-28, N6): a step's words say what the figure is per by the
 *   one rule (utils/unitPriceLabel.perUnitLabel), not the row's raw `per`.
 *
 *   1.6.0 (2026-10-29, N9 — the redesign's callout E, "options collapse when
 *   sources agree"): options giving the same figure are shown as ONE —
 *   optionEntries (a line's options), groupSideOptions' `entries` (a product
 *   group's, the Set popover's). The same figure = the same value per the
 *   line's own unit to 4 dp, above £0, neither option `unitApart` (a figure
 *   per another unit) nor `locked`; for a group, the same figure on every
 *   line, offered on the same lines. The entry stands where its highest
 *   source stands in the ladder; options that differ stay separate, in ladder
 *   order. Choosing it chooses ONE source — the proposal when the proposal is
 *   one of them (so nothing about the proposal, the source recorded, confirm,
 *   undo or the audit row changes), else the highest in the ladder
 *   (`entryChoice`; sideChoices takes its `ids` line by line). Each source
 *   keeps its own words, proposed marker and notes.
 */

import { PILES, SALE_LABELS, COST_LABELS, saleWithRep, money, ukDate } from './pricingCosting';
import { perUnitLabel } from './unitPriceLabel';

/** The pile a row is PLACED by: the one it was held at, else its own. */
export function placeOf(pile, held) {
  return held || pile;
}

/**
 * Start holding a row (an edit began). A row already held keeps the pile it
 * was first held at — the place it had before any of this edit.
 * @param {Record<string,string>} holds  key → pile
 */
export function holdRow(holds, key, pileNow) {
  if (!key || !pileNow || holds[key]) return holds;
  return { ...holds, [key]: pileNow };
}

/**
 * Release the held rows, except `keepKey` (the row the person is still in).
 * @returns {{ holds: Record<string,string>, released: Array<{ key, from }> }}
 */
export function releaseHolds(holds, keepKey = null) {
  const released = [];
  const next = {};
  // One key, or several (a subgroup's lines), or a space-separated list.
  const keep = new Set(Array.isArray(keepKey) ? keepKey : String(keepKey ?? '').split(' ').filter(Boolean));
  Object.entries(holds || {}).forEach(([key, from]) => {
    if (keep.has(key)) next[key] = from;
    else released.push({ key, from });
  });
  return { holds: released.length ? next : holds, released };
}

/**
 * Of the released rows, the ones whose pile is now another: each with where
 * it went. `pileNow(key)` is the row's pile now (null when it is gone).
 */
export function movedOnRelease(released, pileNow) {
  return (released || [])
    .map(r => ({ ...r, to: pileNow(r.key) }))
    .filter(r => r.to && r.to !== r.from);
}

/** "SO-D10123 moved to Check" / "3 lines moved — 2 to Check, 1 to Ready". */
export function movedNote(moved, labelOf, pileLabel) {
  if (!moved.length) return '';
  if (moved.length === 1) return `${labelOf(moved[0].key)} moved to ${pileLabel(moved[0].to)}`;
  const by = new Map();
  moved.forEach(m => by.set(m.to, (by.get(m.to) || 0) + 1));
  return `${moved.length} lines moved — ${[...by.entries()].map(([to, n]) => `${n} to ${pileLabel(to)}`).join(', ')}`;
}

// ---------------------------------------------------------------------------
// Group by customer › product
// ---------------------------------------------------------------------------

const lower = (v) => String(v ?? '').trim().toLowerCase();
const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const cmpText = (a, b) => String(a || '').localeCompare(String(b || ''), 'en-GB', { numeric: true, sensitivity: 'base' });
const EPS = 0.00005;

/**
 * A row's two levels. An order line carries the price request's own keys
 * (`group`, from the server); any other row is a group of its own under its
 * customer.
 */
export function groupKeyOf(v) {
  const customer = v.group?.customer
    || (v.customer?.id ? `acct:${v.customer.id}` : `name:${lower(v.customer?.name)}`);
  const request = v.group?.request || `line:${v.key}`;
  return { customer, request };
}

/** Customers by account number (none last), then name — as the reps' emails. */
function compareCustomer(a, b) {
  const an = String(a?.account_no || '').trim();
  const bn = String(b?.account_no || '').trim();
  if (!an !== !bn) return an ? -1 : 1;
  return cmpText(an, bn) || cmpText(a?.name, b?.name);
}
const firstDay = (rows) => rows.map(r => String(r.delivery_date || '')).filter(Boolean).sort()[0] || '9999';
const compareLines = (a, b) => String(a.delivery_date || '').localeCompare(String(b.delivery_date || ''))
  || cmpText(a.refs?.[0]?.label, b.refs?.[0]?.label) || cmpText(a.key, b.key);

/**
 * The rows as customers, each with its subgroups ("requests"), in the reps'
 * order: (rep, when `repOf` is given), customer by account number then name;
 * subgroups by product, then first delivery; lines by delivery date.
 * @returns {Array<{ key, rep, customer, requests: Array<{ key, rows }>, lineCount }>}
 */
export function buildGroups(rows, { repOf = null } = {}) {
  const blocks = new Map();
  for (const v of rows || []) {
    const k = groupKeyOf(v);
    const rep = repOf ? (repOf(v) || '') : null;
    const bk = `${rep ?? ''}\u0000${k.customer}`;
    if (!blocks.has(bk)) blocks.set(bk, { key: bk, rep, customer: v.customer || {}, requests: new Map(), lineCount: 0 });
    const b = blocks.get(bk);
    if (!b.requests.has(k.request)) b.requests.set(k.request, { key: k.request, rows: [] });
    b.requests.get(k.request).rows.push(v);
    b.lineCount += 1;
  }
  const list = [...blocks.values()].map(b => ({
    ...b,
    requests: [...b.requests.values()]
      .map(r => ({ ...r, rows: [...r.rows].sort(compareLines) }))
      .sort((x, y) => cmpText(x.rows[0].item?.name, y.rows[0].item?.name)
        || cmpText(x.rows[0].item?.code, y.rows[0].item?.code)
        || firstDay(x.rows).localeCompare(firstDay(y.rows))
        || cmpText(x.key, y.key))
  }));
  return list.sort((a, b) => (repOf
    ? ((!a.rep) - (!b.rep)) || cmpText(a.rep, b.rep)
    : 0) || compareCustomer(a.customer, b.customer) || cmpText(a.key, b.key));
}

/**
 * Pages of whole subgroups: about `limit` lines a page, a subgroup never
 * split (one bigger than a page has a page to itself).
 * @returns {Array<Array<{ block, request }>>}
 */
export function pageGroups(blocks, limit) {
  const pages = [];
  let page = [];
  let n = 0;
  for (const block of blocks || []) {
    for (const request of block.requests) {
      const size = request.rows.length;
      if (page.length && n + size > limit) { pages.push(page); page = []; n = 0; }
      page.push({ block, request });
      n += size;
    }
  }
  if (page.length) pages.push(page);
  return pages;
}

/**
 * A side as the screen shows it with an on-screen choice `ov` ({ id } or
 * { id: 'manual', value }) over the proposal. A confirmed side keeps what it
 * has (a choice cannot change it until it is undone).
 */
export function chosenFor(s, ov) {
  if (!s || !s.applies) return s?.chosen ?? null;
  let chosen = s.proposed || null;
  if (ov && !(s.confirmed && !s.reopened)) {
    if (ov.id === 'manual') {
      chosen = { id: 'manual', label: 'Typed by hand', detail: '', value: num(ov.value) };
    } else {
      chosen = (s.options || []).find(o => o.id === ov.id) || s.proposed || null;
    }
  }
  return chosen;
}

/**
 * The line with `choice` applied to one side (null = back to the proposal) —
 * what it will read once the choice lands, so "Set and confirm" can confirm
 * the figure just set without waiting for a render.
 */
export function withChoice(v, side, choice) {
  const s = v?.[side];
  if (!s || !s.applies) return v;
  return { ...v, [side]: { ...s, chosen: chosenFor(s, choice) } };
}

/** A side's figure on one line, and what state it is in. */
export function sideFigure(v, side) {
  const s = v?.[side];
  if (!s || !s.applies) return null;
  if (side === 'sale' && s.locked) return { value: num(s.current), state: 'invoiced' };
  if (s.confirmed && !s.reopened) return { value: num(s.confirmed.value), state: 'confirmed' };
  return { value: num(s.chosen?.value), state: 'open', source: s.chosen?.id || null };
}

const key4 = (x) => Math.round(x * 10000);

/**
 * A side across a group's lines: one figure when they all agree, else the
 * range and how many distinct figures — with how many are confirmed,
 * invoiced, or have none yet.
 */
export function sideSummary(rows, side) {
  const figs = (rows || []).map(v => sideFigure(v, side)).filter(Boolean);
  const values = figs.map(f => f.value).filter(x => x !== null && x > 0);
  const min = values.length ? Math.min(...values) : null;
  const max = values.length ? Math.max(...values) : null;
  const distinct = new Set(values.map(key4)).size;
  const none = figs.filter(f => !(f.value > 0)).length;
  return {
    applies: figs.length,
    min, max,
    distinct,
    same: values.length > 0 && max - min < EPS,
    // Differs: two figures, or a figure on some lines and none on others.
    differs: distinct > 1 || (distinct === 1 && none > 0),
    none,
    confirmed: figs.filter(f => f.state === 'confirmed').length,
    invoiced: figs.filter(f => f.state === 'invoiced').length,
    open: figs.filter(f => f.state === 'open').length
  };
}

/** "Differs · 2 prices" / "Differs · 3 costs, 1 with none" — null when the lines agree. */
export function differsText(sum, side) {
  if (!sum || !sum.differs) return null;
  const word = side === 'cost' ? 'cost' : 'price';
  const parts = [];
  if (sum.distinct > 1) parts.push(`${sum.distinct} ${word}s`);
  if (sum.none) parts.push(`${sum.none} with none`);
  return `Differs · ${parts.join(', ')}`;
}

/** Why a line cannot take a choice made for its group (null = it can). */
export function choiceBlock(v, side) {
  const s = v?.[side];
  if (side === 'cost') {
    if (!s || !s.applies) return 'No cost on this line';
    if (s.confirmed && !s.reopened) return 'Its cost is already confirmed — undo it first';
    return null;
  }
  if (!s || !s.applies) return 'No sale on this line';
  if (s.locked) return 'Invoiced — the sale price is read only';
  if (s.confirmed && !s.reopened) return 'Its sale price is already confirmed — undo it first';
  return null;
}
/** Why a line cannot take a sale price chosen for its subgroup (null = it can). */
export const saleChoiceBlock = (v) => choiceBlock(v, 'sale');

const labelsOf = (side) => (side === 'cost' ? COST_LABELS : SALE_LABELS);

// ---------------------------------------------------------------------------
// Options that give the same figure, shown once (1.6.0, N9)
// ---------------------------------------------------------------------------

/**
 * What an option's figure is for agreeing with others: its value to 4 dp per
 * the line's own unit — null (joins nothing) at £0 or none, for a figure per
 * another unit (`unitApart`) and for a read-only one (`locked`, the invoice).
 */
export function agreeKey(o) {
  const v = num(o?.value);
  if (v === null || !(v > 0) || o.unitApart || o.locked) return null;
  return key4(v);
}

/**
 * Which source of `ids` a line takes when they are chosen together: its
 * proposal when that is one of them (so the proposal, the source recorded,
 * confirm, undo and the audit row are what they would be with the proposal
 * alone), else the highest of them in its ladder (the order of its options).
 * Null when the line offers none of them.
 */
export function entryChoice(s, ids) {
  const want = ids || [];
  if (s?.proposed && want.includes(s.proposed.id)) return s.proposed.id;
  const hit = (s?.options || []).find(o => want.includes(o.id));
  return hit ? hit.id : null;
}

/**
 * A line's options with those that give the same figure as ONE entry, in
 * ladder order: each entry stands where its highest source stands.
 * @param {object} s  a line's side ({ options, proposed })
 * @returns {Array<{ key, id, ids, value, sources, merged, proposedId }>}
 *   `id` the source choosing it takes (entryChoice), `sources` its options in
 *   ladder order, `proposedId` the proposed source when it is one of them.
 */
export function optionEntries(s) {
  const list = [];
  const byFigure = new Map();
  for (const o of s?.options || []) {
    const k = agreeKey(o);
    if (k !== null && byFigure.has(k)) { byFigure.get(k).push(o); continue; }
    const sources = [o];
    list.push(sources);
    if (k !== null) byFigure.set(k, sources);
  }
  const proposedId = s?.proposed?.id ?? null;
  return list.map(sources => {
    const ids = sources.map(o => o.id);
    return {
      key: ids.join('+'),
      id: entryChoice(s, ids),
      ids,
      value: sources[0].value,
      sources,
      merged: sources.length > 1,
      proposedId: ids.includes(proposedId) ? proposedId : null
    };
  });
}

/** The entry a line's chosen source belongs to (its key), or null. */
export function entryOf(entries, id) {
  return (entries || []).find(e => e.ids.includes(id))?.key ?? null;
}

/** "Bill FSIN50376235 · Price change 01/10 · Item cost" — an entry's sources, short. */
export const entrySourcesText = (entry) => (entry?.sources || []).map(o => o.short || o.label).join(' · ');

/**
 * A group's options (groupSideOptions) as entries: two agree when they are
 * offered on the same lines and give the same figure on each of them.
 */
function groupEntries(options) {
  const list = [];
  const same = (a, b) => {
    if (a.agree.size !== b.agree.size) return false;
    for (const [k, fig] of a.agree) {
      if (fig === null || !b.agree.has(k) || b.agree.get(k) !== fig) return false;
    }
    return true;
  };
  for (const o of options) {
    const into = list.find(members => same(members[0], o));
    if (into) into.push(o);
    else list.push([o]);
  }
  return list.map(members => {
    const head = members[0];
    const ids = members.map(o => o.id);
    return {
      key: ids.join('+'),
      id: head.id,
      ids,
      label: members.length > 1 ? members.map(o => o.short || o.label).join(' · ') : head.label,
      sources: members.map(({ agree, ...o }) => o),
      merged: members.length > 1,
      min: head.min, max: head.max, same: head.same, lines: head.lines, all: head.all
    };
  });
}

/** The choice an entry makes for a group's lines ({ id } alone for one source). */
export const entryGroupChoice = (entry) => (entry?.merged ? { id: entry.id, ids: entry.ids } : { id: entry?.id });

/**
 * The options a group offers on one side: each option some of its open lines
 * have, with each line's figure — one figure when they agree ("Contract C1
 * £300, all 3"), else a range, chosen as each line's own. `takers` are the
 * lines that can take a choice; `current` the option every taker has chosen
 * (null when they differ), `manualValue` the one figure typed on all of them.
 */
export function groupSideOptions(rows, side) {
  const takers = (rows || []).filter(v => !choiceBlock(v, side));
  const by = new Map();
  for (const v of takers) {
    for (const o of v[side].options || []) {
      if (!by.has(o.id)) by.set(o.id, { id: o.id, labels: new Set(), shorts: new Set(), values: [], keys: [], agree: new Map() });
      const e = by.get(o.id);
      e.labels.add(o.label);
      e.shorts.add(o.short || o.label);
      if (num(o.value) !== null) e.values.push(num(o.value));
      e.keys.push(v.key);
      e.agree.set(v.key, agreeKey(o));
    }
  }
  const options = [...by.values()].map(e => {
    const min = e.values.length ? Math.min(...e.values) : null;
    const max = e.values.length ? Math.max(...e.values) : null;
    const label = e.labels.size === 1 ? [...e.labels][0] : (labelsOf(side)[e.id] || e.id);
    return {
      id: e.id,
      label,
      short: e.shorts.size === 1 ? [...e.shorts][0] : label,
      min, max,
      same: min !== null && max - min < EPS,
      lines: e.keys.length,
      all: e.keys.length === takers.length,
      // Line by line, the figure it gives (null = joins nothing) — 1.6.0.
      agree: e.agree
    };
  });
  const chosenIds = takers.map(v => v[side].chosen?.id || '');
  const chosen = new Set(chosenIds);
  const manualValues = new Set(takers.filter(v => v[side].chosen?.id === 'manual').map(v => String(v[side].chosen?.value ?? '')));
  const entries = groupEntries(options);
  // The entry on now (1.6.0): on every taker, the source chosen gives the
  // figure the entry gives there — it is one of the entry's sources, or
  // agrees with one of them on that line (the line shows them as one).
  const lineIdsOf = takers.map(v => {
    const c = v[side].chosen?.id || '';
    const own = optionEntries(v[side]).find(en => en.ids.includes(c));
    return own ? own.ids : [c];
  });
  const currentEntry = takers.length && chosenIds.every(Boolean)
    ? (entries.find(en => lineIdsOf.every(ids => ids.some(id => en.ids.includes(id))))?.key || null)
    : null;
  return {
    takers: takers.map(v => v.key),
    blocked: (rows || []).filter(v => choiceBlock(v, side)).map(v => ({ key: v.key, why: choiceBlock(v, side) })),
    options: options.map(({ agree, ...o }) => o),
    entries,
    current: chosen.size === 1 ? [...chosen][0] || null : null,
    currentEntry,
    manualValue: chosen.size === 1 && chosen.has('manual') && manualValues.size === 1 ? [...manualValues][0] : null
  };
}
/** The sale options a subgroup offers (see groupSideOptions). */
export const groupSaleOptions = (rows) => groupSideOptions(rows, 'sale');

/**
 * One choice for a group of lines on one side, line by line: `{ id }` (or
 * `{ id, ids }` for options shown as one, 1.6.0) takes each line's own option
 * of that kind — as the line shows it: with the sources giving the same
 * figure on that line, the proposal when it is one of them, else the highest
 * in the ladder (its proposal → the choice is cleared, so it reads "proposed"), `{ id: 'manual', value }` types the same figure on
 * each. Lines that cannot take it are skipped, each with why.
 * @returns {{ set: Array<{ key, choice: object|null }>, skipped: Array<{ key, why }> }}
 */
export function sideChoices(rows, side, choice) {
  const set = [];
  const skipped = [];
  for (const v of rows || []) {
    const block = choiceBlock(v, side);
    if (block) { skipped.push({ key: v.key, why: block }); continue; }
    if (choice.id === 'manual') { set.push({ key: v.key, choice: { id: 'manual', value: choice.value ?? '' } }); continue; }
    // Options shown as one (1.6.0): `ids` are its sources. The line takes the
    // option IT shows them in (its own sources giving that figure) — its
    // proposal when that is one of them, else the highest in its ladder.
    const first = entryChoice(v[side], choice.ids && choice.ids.length ? choice.ids : [choice.id]);
    if (!first) { skipped.push({ key: v.key, why: `No ${String(labelsOf(side)[choice.id] || 'such option').toLowerCase()} for this line` }); continue; }
    const pick = optionEntries(v[side]).find(en => en.ids.includes(first))?.id || first;
    set.push({ key: v.key, choice: v[side].proposed?.id === pick ? null : { id: pick } });
  }
  return { set, skipped };
}
/** One sale choice for a whole subgroup (see sideChoices). */
export const groupChoices = (rows, choice) => sideChoices(rows, 'sale', choice);

/**
 * What a "Set … for group" opens on, per side: the option (entry — 1.6.0)
 * every taker has chosen (not typed) → that entry's key; one figure typed on all → typed, that
 * figure; else typed, prefilled with the figure the lines share, else the
 * most common proposal (most lines, then the lower figure), else the most
 * common figure shown.
 * @returns {{ mode: string, value: string }}  mode 'manual' or an option id
 */
export function setPrefill(rows, side) {
  const g = groupSideOptions(rows, side);
  // The entry every taker chose a source of (1.6.0: options giving the same
  // figure are one entry, its key — one source's key is its id).
  if (g.currentEntry && g.entries.some(e => e.key === g.currentEntry && e.all)) return { mode: g.currentEntry, value: '' };
  if (g.manualValue !== null && g.manualValue !== '') return { mode: 'manual', value: g.manualValue };
  const takers = (rows || []).filter(v => g.takers.includes(v.key));
  const sum = sideSummary(takers, side);
  if (sum.same && !sum.none) return { mode: 'manual', value: plain(sum.min) };
  const common = (values) => {
    const by = new Map();
    values.filter(x => x !== null && x > 0).forEach(x => by.set(key4(x), (by.get(key4(x)) || 0) + 1));
    const best = [...by.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0];
    return best ? best[0] / 10000 : null;
  };
  const proposed = common(takers.map(v => num(v[side].proposed?.value)));
  if (proposed !== null) return { mode: 'manual', value: plain(proposed) };
  const shown = common(takers.map(v => sideFigure(v, side)?.value ?? null));
  return { mode: 'manual', value: shown === null ? '' : plain(shown) };
}
// A figure as typed: no thousands separators, no trailing zeros past 2dp.
function plain(x) {
  if (x === null || x === undefined) return '';
  const s = (Math.round(x * 10000) / 10000).toFixed(4).replace(/0{1,2}$/, '');
  return s;
}

/** How many of the lines sit in each pile, in the screen's order. */
export function pileCounts(rows) {
  const by = new Map();
  (rows || []).forEach(v => by.set(v.pile, (by.get(v.pile) || 0) + 1));
  return PILES.filter(p => by.has(p)).map(p => [p, by.get(p)]);
}

/** The lines' notes, each said once with how many lines have it — errors first. */
export function flagSummary(rows) {
  const rank = { err: 0, warn: 1, info: 2 };
  const by = new Map();
  for (const v of rows || []) {
    for (const [kind, text] of v.flags || []) {
      if (kind === 'pile') continue;
      const e = by.get(text) || { kind, text, n: 0 };
      e.n += 1;
      by.set(text, e);
    }
  }
  return [...by.values()].sort((a, b) => (rank[a.kind] ?? 3) - (rank[b.kind] ?? 3) || b.n - a.n);
}

/** The cost each line will be confirmed at, as shown — { order line id: cost } (sends to reps). */
export function costsForReps(rows) {
  const out = {};
  for (const v of rows || []) {
    if (v.kind !== 'order_line' || !v.order_line_id) continue;
    const f = sideFigure(v, 'cost');
    if (f && f.value > 0) out[v.order_line_id] = f.value;
  }
  return out;
}

/**
 * A group's delivery dates for its item cell (1.3.0): each date once, in
 * order, comma separated — "09/10, 16/10, 23/10"; "28/12/26, 04/01/27" when
 * the years differ; one date alone in full ("09/10/2026"); "no date" for none.
 * @param {string[]} days  'YYYY-MM-DD' (anything longer is cut to the day)
 */
export function deliveryDatesText(days) {
  const list = [...new Set((days || []).map(d => String(d || '').slice(0, 10)).filter(d => /^\d{4}-\d{2}-\d{2}$/.test(d)))].sort();
  if (!list.length) return 'no date';
  const parts = (d) => { const [y, m, dd] = d.split('-'); return { y, m, dd }; };
  if (list.length === 1) { const { y, m, dd } = parts(list[0]); return `${dd}/${m}/${y}`; }
  const years = new Set(list.map(d => d.slice(0, 4)));
  return list.map(d => {
    const { y, m, dd } = parts(d);
    return years.size > 1 ? `${dd}/${m}/${y.slice(2)}` : `${dd}/${m}`;
  }).join(', ');
}

// ---------------------------------------------------------------------------
// 1.4.0 — what is open: one product at a time; customer sections fold
// ---------------------------------------------------------------------------
//
// The state is { open, openGroups, collapsed }:
//   open        line key → true   a line's cost / sale options are showing
//   openGroups  group key → true  a product group's panel and lines are showing
//   collapsed   customer key → true  the customer's section shows only its heading
// A PRODUCT is one of the customer's rows: a group of several lines (open =
// its key in openGroups) or a single line (open = its options).

const omit = (obj, keys) => {
  const drop = new Set(keys);
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => { if (v && !drop.has(k)) out[k] = true; });
  return out;
};
const stateOf = (st) => ({ open: st?.open || {}, openGroups: st?.openGroups || {}, collapsed: st?.collapsed || {} });

/** One { block, request } entry of a page (buildGroups / pageGroups) as a product. */
export function productOf(entry) {
  const rows = entry?.request?.rows || [];
  return { key: entry?.request?.key, lines: rows.map(r => r.key), single: rows.length === 1, customer: entry?.block?.key };
}

/** Every product on the pages, by each of its line keys. */
export function productsByLine(pages) {
  const map = new Map();
  (pages || []).forEach(page => (page || []).forEach(entry => {
    const p = productOf(entry);
    p.lines.forEach(k => map.set(k, p));
  }));
  return map;
}

/** Every customer section on the pages, each once, with its products. */
export function customerSections(pages) {
  const map = new Map();
  (pages || []).forEach(page => (page || []).forEach(entry => {
    const ck = entry.block.key;
    if (!map.has(ck)) map.set(ck, { key: ck, products: [] });
    const sec = map.get(ck);
    const p = productOf(entry);
    if (!sec.products.some(x => x.key === p.key)) sec.products.push(p);
  }));
  return [...map.values()];
}

export function isProductOpen(st, product) {
  if (!product) return false;
  const s = stateOf(st);
  return product.single ? !!s.open[product.lines[0]] : !!s.openGroups[product.key];
}

/**
 * Open a product and close every other (any customer). The product's own
 * lines keep their options as they were; customer sections are untouched.
 */
export function openProduct(st, product) {
  const s = stateOf(st);
  const mine = new Set(product.lines);
  const open = {};
  Object.keys(s.open).forEach(k => { if (s.open[k] && mine.has(k)) open[k] = true; });
  if (product.single) open[product.lines[0]] = true;
  return { ...s, open, openGroups: product.single ? {} : { [product.key]: true } };
}

/** Close a product: its panel and every one of its lines' options. */
export function closeProduct(st, product) {
  const s = stateOf(st);
  return { ...s, open: omit(s.open, product.lines), openGroups: omit(s.openGroups, [product.key]) };
}

export function toggleProduct(st, product) {
  return isProductOpen(st, product) ? closeProduct(st, product) : openProduct(st, product);
}

/**
 * A line's options opened or closed. A line on its own is its product; a
 * line inside a group opens within that group, which becomes the one open
 * product (so any other closes).
 */
export function toggleLine(st, key, product) {
  const p = product || { key: `line:${key}`, lines: [key], single: true };
  if (p.single) return toggleProduct(st, p);
  const s = stateOf(st);
  if (s.open[key]) return { ...s, open: omit(s.open, [key]) };
  const next = openProduct(s, p);
  return { ...next, open: { ...next.open, [key]: true } };
}

export const isCustomerCollapsed = (st, ck) => !!stateOf(st).collapsed[ck];

/** Fold a customer to its heading — and close every product within it. */
export function collapseCustomer(st, section) {
  let s = stateOf(st);
  (section.products || []).forEach(p => { s = closeProduct(s, p); });
  return { ...s, collapsed: { ...s.collapsed, [section.key]: true } };
}

/** Unfold a customer: its products show, each as it was (closed — never opened by this). */
export function expandCustomer(st, ck) {
  const s = stateOf(st);
  return { ...s, collapsed: omit(s.collapsed, [ck]) };
}

export function toggleCustomer(st, section) {
  return isCustomerCollapsed(st, section.key) ? expandCustomer(st, section.key) : collapseCustomer(st, section);
}

/** Collapse all: every customer a single row, nothing open. */
export function collapseAllCustomers(st, customerKeys) {
  return { open: {}, openGroups: {}, collapsed: Object.fromEntries((customerKeys || []).map(k => [k, true])) };
}

/** Expand all: every customer open, showing its products closed. */
export function expandAllCustomers() {
  return { open: {}, openGroups: {}, collapsed: {} };
}

/** Are all these customers' sections open (the Expand / Collapse all button reads "Collapse all")? */
export function allCustomersExpanded(st, customerKeys) {
  const s = stateOf(st);
  return (customerKeys || []).length > 0 && customerKeys.every(k => !s.collapsed[k]);
}

/**
 * A customer heading's summary, readable with the section folded: products,
 * lines, quantity by unit, the sale value of the lines with a price, and the
 * piles.
 * @returns {{ products, lines, quantities: Array<[unit, total]>, saleTotal, priced, piles }}
 */
export function customerSummary(block) {
  const rows = (block?.requests || []).flatMap(r => r.rows);
  const byUnit = new Map();
  let saleTotal = 0;
  let priced = 0;
  let saleLines = 0;
  rows.forEach(v => {
    const q = num(v.qty);
    if (q !== null) byUnit.set(v.unit || '', (byUnit.get(v.unit || '') || 0) + q);
    // A split line invoiced on another order has no sale of its own; the line
    // taking it sells its tonnage too (utils/tonnageAdditions, 23 Oct 2026).
    if (v.sale && v.sale.applies === false && v.sale.addedTo) return;
    saleLines += 1;
    const added = (v.tonnage?.added || []).reduce((t, a) => t + (num(a.quantity) || 0), 0);
    const p = num(v.saleValue);
    if (p !== null && p > 0 && q !== null) { saleTotal += p * (q + added); priced += 1; }
  });
  return {
    products: (block?.requests || []).length,
    lines: rows.length,
    saleLines,
    quantities: [...byUnit.entries()].sort((a, b) => (a[0] === 'T' ? -1 : b[0] === 'T' ? 1 : cmpText(a[0], b[0]))),
    saleTotal: Math.round(saleTotal * 100) / 100,
    priced,
    piles: pileCounts(rows)
  };
}

// ---------------------------------------------------------------------------
// The four-step strip (D2): Cost · Sale · Confirmed · Invoiced
// ---------------------------------------------------------------------------

export const STEPS = [
  { key: 'cost', label: 'Cost', icon: 'shopping_cart' },
  { key: 'sale', label: 'Sale', icon: 'sell' },
  { key: 'confirmed', label: 'Confirmed', icon: 'verified' },
  { key: 'invoiced', label: 'Invoiced', icon: 'receipt_long' }
];
/** The words for a step's state, for hover text and screen readers. */
export const STEP_STATE_WORDS = {
  done: 'done', rep: 'with a rep', warn: 'needs a look', todo: 'not yet', part: 'part done', na: 'not on this line'
};

const sideDoneV = (x) => !x || !x.applies || !!x.locked || (!!x.confirmed && !x.reopened);
const firstFlag = (v, side) => (v.flags || []).find(f => f[2] === side && f[0] !== 'info');
const daysSince = (at, today) => {
  const a = String(at || '').slice(0, 10);
  const b = String(today || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(a) || !/^\d{4}-\d{2}-\d{2}$/.test(b)) return null;
  return Math.round((Date.parse(b) - Date.parse(a)) / 86400000);
};

/**
 * Where a line's sale price is with a rep, in words: "With Andy Hawken since
 * 20/10 (8 days)", "Andy Hawken's £320.00/T held for a second check", "Andy
 * Hawken's price £320.00/T — not approved yet". null when not with a rep.
 * @param {object} v  a row as the screen holds it (sale.chosen applied)
 * @param {string} [per]  the line's unit, e.g. "/T"
 */
export function repSaleText(v, per = '', today = null) {
  const s = v?.sale || {};
  const rep = s.asked?.rep || v?.rep?.name || 'the rep';
  if (!s.applies || sideDoneV(s)) return null;
  if (s.repHeld && s.repReply) return `${rep}'s ${money(s.repReply.price)}${per} held for a second check`;
  if (saleWithRep(v)) {
    const d = s.asked?.at ? daysSince(s.asked.at, today || new Date().toISOString().slice(0, 10)) : null;
    return `With ${rep}${s.asked?.at ? ` since ${ukDate(s.asked.at).slice(0, 5)}` : ''}${d !== null && d >= 0 ? ` (${d} day${d === 1 ? '' : 's'})` : ''}`;
  }
  if (s.chosen && s.chosen.id === 'rep' && s.repReply && s.repReply.status !== 'approved') {
    return `${rep}'s price ${money(s.repReply.price)}${per} — not approved yet`;
  }
  return null;
}

/**
 * One line's four steps (D2). Each { key, label, state, why }: state 'done'
 * (green), 'rep' (blue), 'warn' (amber — a flag on that side, reopened, a
 * draft to approve, a rep's flag), 'todo' (not yet), 'na' (not on this line).
 * @param {object} v  a row as the screen holds it (chosen applied, flags, pile)
 * @param {{ seeCosts?: boolean, today?: string }} [o]
 */
export function lineSteps(v, o = {}) {
  const per = perUnitLabel(v).text;
  const c = v.cost || {};
  const s = v.sale || {};
  const out = [];

  // Cost
  let cost;
  if (!c.applies) {
    cost = v.kind === 'invoice_line'
      ? { state: 'na', why: "A till sale — the till holds its cost; there is none to confirm here" }
      : { state: 'na', why: o.seeCosts === false ? 'Costs are hidden — needs permission to see costs' : 'No cost on this line' };
  } else if (c.confirmed && !c.reopened) {
    cost = { state: 'done', why: `Cost confirmed at ${money(c.confirmed.value)}${per}${c.confirmed.by ? ` by ${c.confirmed.by}` : ''}` };
  } else if (c.reopened) {
    cost = { state: 'warn', why: `Cost reopened: ${c.reopened}` };
  } else if (c.chosen && num(c.chosen.value) > 0) {
    const f = firstFlag(v, 'cost');
    cost = f ? { state: 'warn', why: f[1] } : { state: 'done', why: `Cost ${money(c.chosen.value)}${per} (${c.chosen.label}) — not confirmed yet` };
  } else {
    cost = { state: 'todo', why: 'No cost to propose — type one' };
  }
  out.push({ ...STEPS[0], ...cost });

  // Sale
  let sale;
  const repText = repSaleText(v, per, o.today);
  if (!s.applies) {
    sale = { state: 'na', why: s.addedTo ? s.addedTo.note : 'A stock purchase — no sale' };
  } else if (s.repFlag) {
    sale = { state: 'warn', why: (firstFlag(v, 'sale') || [null, 'Flagged by a rep'])[1] };
  } else if (s.locked) {
    sale = { state: 'done', why: `Invoiced at ${money(s.current)}${per}` };
  } else if (s.confirmed && !s.reopened) {
    sale = { state: 'done', why: `Sale price confirmed at ${money(s.confirmed.value)}${per}${s.confirmed.by ? ` by ${s.confirmed.by}` : ''}` };
  } else if (s.reopened) {
    sale = { state: 'warn', why: `Price reopened: ${s.reopened}` };
  } else if (repText) {
    sale = { state: 'rep', why: repText };
  } else if (s.chosen && num(s.chosen.value) > 0) {
    const f = firstFlag(v, 'sale');
    sale = f ? { state: 'warn', why: f[1] } : { state: 'done', why: `Price ${money(s.chosen.value)}${per} (${s.chosen.label}) — not confirmed yet` };
  } else if (s.chosen) {
    sale = { state: 'warn', why: (firstFlag(v, 'sale') || [null, 'The price is £0'])[1] };
  } else {
    sale = { state: 'todo', why: 'No price to propose — send it to the rep, or type one' };
  }
  out.push({ ...STEPS[1], ...sale });

  // Confirmed: every side that applies
  const sides = [c, s].filter(x => x && x.applies);
  let confirmed;
  if (!sides.length) confirmed = { state: 'na', why: 'Nothing to confirm here' };
  else if ((c.applies && c.reopened) || (s.applies && s.reopened && !s.locked)) confirmed = { state: 'warn', why: 'Reopened — confirm again' };
  else if (sides.every(sideDoneV)) confirmed = { state: 'done', why: 'Cost and sale confirmed' };
  else {
    const doneSides = [c.applies && sideDoneV(c) ? 'cost' : null, s.applies && sideDoneV(s) ? 'sale price' : null].filter(Boolean);
    confirmed = { state: 'todo', why: doneSides.length ? `The ${doneSides[0]} is confirmed; the ${doneSides[0] === 'cost' ? 'sale price' : 'cost'} is not yet` : 'Not confirmed yet' };
  }
  out.push({ ...STEPS[2], ...confirmed });

  // Invoiced
  let inv;
  if (v.kind === 'po_line') inv = { state: 'na', why: 'A stock purchase — not invoiced' };
  else if (v.invoice || s.locked) inv = { state: 'done', why: `Invoiced${v.invoice?.invoice_no ? ` — ${v.invoice.invoice_no}` : ''}` };
  else if (v.draft) inv = { state: 'warn', why: `Draft ${v.draft.invoice_no} waiting for approval` };
  else if (s.addedTo) inv = { state: 'na', why: s.addedTo.note };
  else inv = { state: 'todo', why: v.status?.delivered ? 'Delivered — invoice once every line of the order is confirmed' : 'Not invoiced yet' };
  out.push({ ...STEPS[3], ...inv });
  return out;
}

/**
 * A group's steps (D2): per step, how many of its lines are done — "2/3" —
 * amber when any needs a look, blue when any is with a rep, green when all
 * are done. `text` is the "2/3" (null when 0 or all).
 */
export function groupSteps(rows, o = {}) {
  const per = (rows || []).map(v => lineSteps(v, o));
  return STEPS.map((st, i) => {
    const states = per.map(p => p[i].state).filter(x => x !== 'na');
    const n = states.length;
    const d = states.filter(x => x === 'done').length;
    const w = states.filter(x => x === 'warn').length;
    const r = states.filter(x => x === 'rep').length;
    let state;
    if (!n) state = 'na';
    else if (d === n) state = 'done';
    else if (w) state = 'warn';
    else if (r) state = 'rep';
    else if (d) state = 'part';
    else state = 'todo';
    const bits = [`${d} of ${n} done`, w ? `${w} to look at` : null, r ? `${r} with a rep` : null].filter(Boolean);
    return {
      ...st, state, text: d > 0 && d < n ? `${d}/${n}` : null,
      why: n ? `${st.label}: ${bits.join(', ')}` : `${st.label}: not on these lines`
    };
  });
}

// ---------------------------------------------------------------------------
// The date range (Cam, 28 Oct 2026): this month, two before, two after, custom
// ---------------------------------------------------------------------------

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const pad2 = (n) => String(n).padStart(2, '0');
/** 'YYYY-MM' → { from, to, label } of that calendar month. */
export function monthRangeOf(ym) {
  const [y, m] = String(ym).split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${y}-${pad2(m)}-01`, to: `${y}-${pad2(m)}-${pad2(last)}`, label: `${MONTHS[m - 1]} ${y}` };
}
const addMonths = (ym, k) => {
  const [y, m] = String(ym).split('-').map(Number);
  const t = (y * 12 + (m - 1)) + k;
  return `${Math.floor(t / 12)}-${pad2((t % 12) + 1)}`;
};

/**
 * The period dropdown's choices for `today` ('YYYY-MM-DD'): This month (named),
 * the two months before and the two after by name, Custom — each with its
 * dates (DateRangeFilter takes a preset carrying from / to as it is). A named
 * month remembered from before, outside these five, is added so it still reads.
 */
export function rangeOptions(today, keep = null) {
  const ym = String(today).slice(0, 7);
  const cur = monthRangeOf(ym);
  const out = [{ value: 'this-month', label: `${cur.label} — this month`, from: cur.from, to: cur.to }];
  [-2, -1, 1, 2].forEach(k => {
    const m = addMonths(ym, k);
    const r = monthRangeOf(m);
    out.push({ value: `month:${m}`, label: r.label, from: r.from, to: r.to });
  });
  // In calendar order: the two before, this month, the two after.
  out.sort((a, b) => a.from.localeCompare(b.from));
  if (keep && /^month:\d{4}-\d{2}$/.test(keep) && !out.some(o => o.value === keep)) {
    const r = monthRangeOf(keep.slice(6));
    out.push({ value: keep, label: r.label, from: r.from, to: r.to });
  }
  out.push({ value: 'custom', label: 'Custom' });
  return out;
}

/**
 * The range a remembered choice means today: 'this-month' follows the
 * calendar; 'month:YYYY-MM' is that month; 'custom' its own dates. Anything
 * unreadable is this month.
 * @param {{ preset, from?, to? }|null} pref
 */
export function rangeOf(pref, today) {
  const ym = String(today).slice(0, 7);
  const p = pref || {};
  if (/^month:\d{4}-\d{2}$/.test(p.preset || '')) {
    const r = monthRangeOf(p.preset.slice(6));
    return { preset: p.preset, from: r.from, to: r.to };
  }
  const isDay = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ''));
  if (p.preset === 'custom' && isDay(p.from) && isDay(p.to)) return { preset: 'custom', from: p.from, to: p.to };
  const r = monthRangeOf(ym);
  return { preset: 'this-month', from: r.from, to: r.to };
}

/** The server takes at most 100 days. */
export const MAX_RANGE_DAYS = 100;
/** Why a range cannot be loaded (null = it can). */
export function rangeBlock(from, to) {
  const isDay = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ''));
  if (!isDay(from) || !isDay(to)) return 'Choose a From and a To date';
  if (from > to) return 'The From date is after the To date';
  const days = Math.round((Date.parse(to) - Date.parse(from)) / 86400000) + 1;
  if (days > MAX_RANGE_DAYS) return `At most ${MAX_RANGE_DAYS} days at a time — this is ${days}`;
  return null;
}
